import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useState } from "react";
import { DrawerState } from "../types/DrawerState";
import GlobalDrawer from "../components/GlobalDrawer/GlobalDrawer";

export type DrawerContextProps = {
  drawerState: DrawerState | null;
  setDrawerState: ((drawerState: DrawerState|null) => void);
  closeDrawer: () => void;
  setDrawerTitle: Dispatch<SetStateAction<string>>;
  drawerContentNode: HTMLDivElement | null;
  drawerActionsNode: HTMLDivElement | null;
  drawerHeaderNode: HTMLDivElement | null;
};

const DrawerProviderContext = createContext<DrawerContextProps | null>(null);

export const DrawerProvider = ({ children }: { children: ReactNode }) => {
  const [drawerState, setDrawerState] = useState<DrawerState | null>(null);
  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [drawerContentNode, setDrawerContentNode] = useState<HTMLDivElement | null>(null);
  const [drawerActionsNode, setDrawerActionsNode] = useState<HTMLDivElement | null>(null);
  const [drawerHeaderNode, setDrawerHeaderNode] = useState<HTMLDivElement | null>(null);
  const [drawerKey, setDrawerKey] = useState("");

  const setDrawerStateExtended = (nextDrawerState: DrawerState|null) => {
    // Fires the outgoing drawer's onClose on every close or replace, never on the initial open.
    if (drawerState && drawerState !== nextDrawerState) {
      drawerState.onClose?.();
    }
    setDrawerState(nextDrawerState);
    setDrawerKey(crypto.randomUUID())
  };

  const closeDrawer = () => {
    setDrawerStateExtended(null);
  };

  return (
    <DrawerProviderContext.Provider
      value={{
        drawerState,
        setDrawerState: setDrawerStateExtended,
        closeDrawer,
        drawerActionsNode,
        drawerContentNode,
        drawerHeaderNode,
        setDrawerTitle,
      }}
    >
      {children}
      <GlobalDrawer
        drawerTitle={drawerTitle}
        setDrawerContentNode={setDrawerContentNode}
        setDrawerActionsNode={setDrawerActionsNode}
        setDrawerHeaderNode={setDrawerHeaderNode}
        key={drawerKey}
      />
    </DrawerProviderContext.Provider>
  );
};

export const useDrawer = () => {
  const context = useContext(DrawerProviderContext);
  if (!context) {
    throw new Error("useDrawer must be used within a DrawerProvider");
  }
  return context;
};

export const useOptionalDrawer = () => {
  const context = useContext(DrawerProviderContext);
  return context || null;
};
